import React, { useMemo, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, Activity, Target, Shield, Zap, Award, BarChart3, LineChartIcon } from 'lucide-react';
import { portfolioPerformanceData } from '../utils/constants';
interface PortfolioPerformanceProps {
  language?: 'en' | 'vi';
}
const PortfolioPerformance: React.FC<PortfolioPerformanceProps> = memo(({
  language = 'en'
}) => {
  const [chartView, setChartView] = useState<'cumulative' | 'excess'>('cumulative');
  const translations = useMemo(() => ({
    en: {
      title: 'Personal',
      titleHighlight: 'Portfolio Tracking',
      subtitle: '58-Day Performance Record (Dec 2024 - Mar 2025)',
      totalReturn: 'Total Return',
      annualizedReturn: 'Annualized Return',
      sharpeRatio: 'Sharpe Ratio',
      maxDrawdown: 'Max Drawdown',
      volatility: 'Volatility',
      alpha: 'Alpha',
      beta: 'Beta',
      winRate: 'Win Rate',
      myPortfolio: 'Portfolio',
      spy: 'SPY Benchmark',
      excess: 'Relative Return',
      vsSpyLabel: 'vs SPY',
      preliminary: 'Limited Sample - 58 Days',
      withinRange: 'Observed',
      keyMetrics: 'Performance Metrics',
      riskMetrics: 'Risk Metrics',
      performanceChart: 'Performance Comparison',
      cumulativeReturns: 'Cumulative',
      excessReturns: 'Relative',
      disclaimer: 'Past performance is not indicative of future results. This represents a limited 58-day sample period and should not be extrapolated.',
      indexedTo100: 'Indexed to 100 on 12/14/24'
    },
    vi: {
      title: 'Theo Dõi Danh Mục',
      titleHighlight: 'Cá Nhân',
      subtitle: 'Ghi Nhận 58 Ngày (Tháng 12/2024 - Tháng 3/2025)',
      totalReturn: 'Lợi Nhuận Tổng',
      annualizedReturn: 'Lợi Nhuận Hàng Năm',
      sharpeRatio: 'Tỷ Lệ Sharpe',
      maxDrawdown: 'Sụt Giảm Tối Đa',
      volatility: 'Biến Động',
      alpha: 'Alpha',
      beta: 'Beta',
      winRate: 'Tỷ Lệ Thắng',
      myPortfolio: 'Danh Mục',
      spy: 'Chỉ Số SPY',
      excess: 'Lợi Nhuận Tương Đối',
      vsSpyLabel: 'so với SPY',
      preliminary: 'Mẫu Hạn Chế - 58 Ngày',
      withinRange: 'Quan Sát',
      keyMetrics: 'Chỉ Số Hiệu Suất',
      riskMetrics: 'Chỉ Số Rủi Ro',
      performanceChart: 'So Sánh Hiệu Suất',
      cumulativeReturns: 'Tích Lũy',
      excessReturns: 'Tương Đối',
      disclaimer: 'Hiệu suất trong quá khứ không phản ánh kết quả tương lai. Đây là mẫu 58 ngày hạn chế và không nên ngoại suy.',
      indexedTo100: 'Chỉ số hóa về 100 ngày 12/14/24'
    }
  }), []);
  const t = translations[language];
  const {
    metrics,
    dailyReturns
  } = portfolioPerformanceData;
  const keyMetrics = useMemo(() => [{
    icon: TrendingUp,
    label: t.totalReturn,
    value: `${metrics.totalReturn.fund.toFixed(2)}%`,
    benchmark: `${metrics.totalReturn.benchmark.toFixed(2)}%`,
    difference: metrics.totalReturn.difference,
    color: 'from-green-500 to-emerald-500',
    status: t.preliminary
  }, {
    icon: Activity,
    label: t.annualizedReturn,
    value: `${metrics.annualizedReturn.fund.toFixed(2)}%`,
    benchmark: `${metrics.annualizedReturn.benchmark.toFixed(2)}%`,
    difference: metrics.annualizedReturn.difference,
    color: 'from-blue-500 to-cyan-500',
    status: t.preliminary
  }, {
    icon: Target,
    label: t.sharpeRatio,
    value: metrics.sharpeRatio.fund.toFixed(2),
    benchmark: metrics.sharpeRatio.benchmark.toFixed(2),
    difference: metrics.sharpeRatio.difference,
    color: 'from-purple-500 to-pink-500',
    status: t.preliminary
  }, {
    icon: Shield,
    label: t.maxDrawdown,
    value: `${metrics.maxDrawdown.fund.toFixed(2)}%`,
    benchmark: `${metrics.maxDrawdown.benchmark.toFixed(2)}%`,
    difference: metrics.maxDrawdown.difference,
    color: 'from-orange-500 to-red-500',
    status: t.withinRange,
    inverse: true
  }], [metrics, t]);
  const riskMetrics = useMemo(() => [{
    icon: Zap,
    label: t.volatility,
    value: `${metrics.volatility.fund.toFixed(2)}%`,
    benchmark: `${metrics.volatility.benchmark.toFixed(2)}%`,
    difference: metrics.volatility.difference,
    status: t.withinRange
  }, {
    icon: Award,
    label: t.alpha,
    value: `${metrics.alpha.fund.toFixed(2)}%`,
    benchmark: `${metrics.alpha.benchmark.toFixed(2)}%`,
    difference: metrics.alpha.difference,
    status: t.preliminary
  }, {
    icon: BarChart3,
    label: t.beta,
    value: metrics.beta.fund.toFixed(2),
    benchmark: metrics.beta.benchmark.toFixed(2),
    difference: metrics.beta.difference,
    status: t.withinRange
  }, {
    icon: TrendingUp,
    label: t.winRate,
    value: `${metrics.winRate.fund.toFixed(2)}%`,
    benchmark: `${metrics.winRate.benchmark.toFixed(2)}%`,
    difference: metrics.winRate.difference,
    status: t.withinRange
  }], [metrics, t]);
  const CustomTooltip = useMemo(() => ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-slate-900/95 backdrop-blur-sm border border-white/10 rounded-lg p-3 shadow-xl" data-id="element-1077">
                <p className="text-white text-sm font-medium mb-2" data-id="element-1078">{label}</p>
                {payload.map((entry: any, index: number) => <p key={index} className="text-sm" style={{
          color: entry.color
        }} data-id="element-1079">
                    {entry.name}: {entry.value.toFixed(2)}
                    {chartView === 'excess' ? '%' : ''}
                  </p>)}
              </div>;
    }
    return null;
  }, [chartView]);
  return <section id="portfolio-performance" className="w-full py-12 md:py-24 bg-gradient-to-br from-background via-secondary to-background relative overflow-hidden" data-id="element-1080">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1081">
          <div className="absolute top-1/4 -left-48 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" data-id="element-1082" />
          <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" data-id="element-1083" />
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-1084">
          <div className="text-center mb-8 md:mb-16" data-id="element-1085">
            <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-display font-bold mb-4" data-id="element-1086">
              {t.title}{' '}
              <span className="gradient-text" data-id="element-1087">{t.titleHighlight}</span>
            </motion.h2>
            <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg md:text-xl text-muted max-w-2xl mx-auto" data-id="element-1088">
              {t.subtitle}
            </motion.p>
          </div>

          <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-secondary/30 rounded-2xl p-4 md:p-8 border border-white/10 mb-8 md:mb-12" data-id="element-1089">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4" data-id="element-1090">
              <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2" data-id="element-1091">
                <LineChartIcon className="text-blue-500" size={24} data-id="element-1092" />
                {t.performanceChart}
              </h3>

              <div className="flex gap-2 w-full md:w-auto" data-id="element-1093">
                <button onClick={() => setChartView('cumulative')} className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-medium transition-all text-sm ${chartView === 'cumulative' ? 'bg-blue-600 text-white shadow-lg' : 'bg-background/50 text-muted hover:bg-background'}`} data-id="element-1094">
                  {t.cumulativeReturns}
                </button>
                <button onClick={() => setChartView('excess')} className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-medium transition-all text-sm ${chartView === 'excess' ? 'bg-green-600 text-white shadow-lg' : 'bg-background/50 text-muted hover:bg-background'}`} data-id="element-1095">
                  {t.excessReturns}
                </button>
              </div>
            </div>

            <div className="w-full h-[300px] md:h-[400px]" data-id="element-1096">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1097">
                {chartView === 'cumulative' ? <LineChart data={dailyReturns} margin={{
              top: 5,
              right: 10,
              left: 0,
              bottom: 5
            }} data-id="element-1098">
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" data-id="element-1099" />
                    <XAxis dataKey="date" stroke="#94a3b8" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} interval="preserveStartEnd" data-id="element-1100" />
                    <YAxis stroke="#94a3b8" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} domain={[98, 120]} data-id="element-1101" />
                    <Tooltip content={<CustomTooltip data-id="element-1103" />} data-id="element-1102" />
                    <Legend wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px'
              }} iconType="line" data-id="element-1104" />
                    <Line type="monotone" dataKey="fund" stroke="#10b981" strokeWidth={2} dot={false} name={t.myPortfolio} isAnimationActive={false} data-id="element-1105" />
                    <Line type="monotone" dataKey="spy" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={false} name={t.spy} isAnimationActive={false} data-id="element-1106" />
                  </LineChart> : <AreaChart data={dailyReturns} margin={{
              top: 5,
              right: 10,
              left: 0,
              bottom: 5
            }} data-id="element-1107">
                    <defs data-id="element-1108">
                      <linearGradient id="excessGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-1109">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1110" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-1111" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" data-id="element-1112" />
                    <XAxis dataKey="date" stroke="#94a3b8" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} interval="preserveStartEnd" data-id="element-1113" />
                    <YAxis stroke="#94a3b8" tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} data-id="element-1114" />
                    <Tooltip content={<CustomTooltip data-id="element-1116" />} data-id="element-1115" />
                    <Legend wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px'
              }} iconType="line" data-id="element-1117" />
                    <Area type="monotone" dataKey="excess" stroke="#10b981" strokeWidth={2} fill="url(#excessGradient)" name={t.excess} isAnimationActive={false} data-id="element-1118" />
                  </AreaChart>}
              </ResponsiveContainer>
            </div>

            <p className="text-xs md:text-sm text-muted text-center mt-4" data-id="element-1119">
              {t.indexedTo100}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12" data-id="element-1120">
            {keyMetrics.map((metric, index) => {
          const Icon = metric.icon;
          const isPositive = metric.inverse ? metric.difference < 0 : metric.difference > 0;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10" data-id="element-1121">
                  <div className="flex items-center justify-between mb-4" data-id="element-1122">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${metric.color} flex items-center justify-center`} data-id="element-1123">
                      <Icon size={20} className="text-white" data-id="element-1124" />
                    </div>
                    {isPositive ? <TrendingUp className="text-green-500" size={18} data-id="element-1125" /> : <TrendingDown className="text-red-500" size={18} data-id="element-1126" />}
                  </div>

                  <h3 className="text-xs md:text-sm text-muted mb-2" data-id="element-1127">
                    {metric.label}
                  </h3>
                  <div className="text-2xl md:text-3xl font-bold mb-1" data-id="element-1128">
                    {metric.value}
                  </div>
                  <div className="text-xs text-muted mb-2" data-id="element-1129">
                    {t.vsSpyLabel} {metric.benchmark}
                  </div>

                  <div className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`} data-id="element-1130">
                    {isPositive ? '+' : ''}
                    {metric.difference.toFixed(2)}
                    {metric.label.includes('Ratio') ? '' : '%'}
                  </div>

                  <div className="mt-3 pt-3 border-t border-white/10" data-id="element-1131">
                    <span className="text-xs text-muted" data-id="element-1132">{metric.status}</span>
                  </div>
                </motion.div>;
        })}
          </div>

          <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-secondary/30 rounded-2xl p-4 md:p-8 border border-white/10 mb-8" data-id="element-1133">
            <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2" data-id="element-1134">
              <Shield className="text-blue-500" size={24} data-id="element-1135" />
              {t.riskMetrics}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-1136">
              {riskMetrics.map((metric, index) => {
            const Icon = metric.icon;
            const isPositive = metric.difference > 0;
            return <div key={index} className="bg-background/50 rounded-xl p-4 border border-white/5" data-id="element-1137">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1138">
                      <Icon className="text-accent-cyan" size={18} data-id="element-1139" />
                      <span className="text-xs md:text-sm text-muted" data-id="element-1140">
                        {metric.label}
                      </span>
                    </div>

                    <div className="text-xl md:text-2xl font-bold mb-1" data-id="element-1141">
                      {metric.value}
                    </div>
                    <div className="text-xs text-muted mb-2" data-id="element-1142">
                      {t.vsSpyLabel} {metric.benchmark}
                    </div>

                    <div className={`text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`} data-id="element-1143">
                      {isPositive ? '+' : ''}
                      {metric.difference.toFixed(2)}
                      {metric.label.includes('Ratio') || metric.label.includes('Beta') ? '' : '%'}
                    </div>

                    <div className="mt-2 pt-2 border-t border-white/5" data-id="element-1144">
                      <span className="text-xs text-muted" data-id="element-1145">
                        {metric.status}
                      </span>
                    </div>
                  </div>;
          })}
            </div>
          </motion.div>

          <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-1146">
            <p className="text-xs md:text-sm text-muted max-w-3xl mx-auto" data-id="element-1147">
              {t.disclaimer}
            </p>
          </motion.div>
        </div>
      </section>;
});
PortfolioPerformance.displayName = 'PortfolioPerformance';
export default PortfolioPerformance;